import React, { useState, useEffect, useRef } from "react";

import BcSepIcon from "../../asset/images/about/breadcrumb-separator.svg";

// Desktop + Mobile hero images — imported separately
import HeroBgImg       from "../../asset/images/products/hfps/hero-bg.webp";
import HeroBgImgMobile from "../../asset/images/products/hfps/hero-bg-mobile.webp";

// Slider images
import SlideImg1 from "../../asset/images/products/hfps/slide-1.webp";
import SlideImg2 from "../../asset/images/products/hfps/slide-2.webp";


const SLIDES = [SlideImg1, SlideImg2];
const AUTOPLAY_SPEED = 10000;

const ADVANTAGES = [
  "Higher Power Levels.",
  "Faster Spark Control.",
  "Higher Collection Efficiency",
  "Energy Savings",
  "Better Power Factor",
];

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .hfps-wrap { font-family: 'Poppins', sans-serif; color: #202020; background: #fff; }

  /* ══════════════════════════════════════════
     1. HERO
  ══════════════════════════════════════════ */
  .hfps-hero {
    position: relative;
    width: 100%;
    height: 460px;
    overflow: hidden;
    background: #111;
  }
  .hfps-hero-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }
  .hfps-hero-bg-mobile {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: none;
  }
  .hfps-hero-overlay { position: absolute; inset: 0; }
  .hfps-hero-content {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 102px;
    max-width: 1600px;
    margin: 0 auto;
    left: 0; right: 0;
  }
  .hfps-hero-title {
    font-family: 'Poppins', sans-serif;
    font-size: 42px;
    font-weight: 600;
    color: #fff;
    line-height: 120%;
    margin-bottom: 15px;
    max-width: 480px;
  }
  .hfps-hero-divider {
    width: 563px;
    height: 2px;
    background: #EB2019;
    border: none;
    margin-bottom: 18px;
  }
  .hfps-hero-desc {
    font-family: 'Poppins', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #FFFFFF;
    line-height: 150%;
    max-width: 563px;
  }

  /* ══════════════════════════════════════════
     2. BREADCRUMB
  ══════════════════════════════════════════ */
  .hfps-breadcrumb { padding: 40px 0 0 0; }
  .hfps-breadcrumb-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 102px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .hfps-bc-item {
    font-family: 'Poppins', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #202020;
    text-decoration: none;
    transition: color 0.15s;
  }
  .hfps-bc-item:hover { color: #EB2019; }
  .hfps-bc-item.hfps-active { color: #202020; }
  .hfps-bc-sep {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    margin: 0 8px;
    flex-shrink: 0;
  }

  /* ══════════════════════════════════════════
     SHARED INNER
  ══════════════════════════════════════════ */
  .hfps-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 102px;
  }

  /* ══════════════════════════════════════════
     3. CONTENT SECTION
  ══════════════════════════════════════════ */
  .hfps-section-content {
    padding: 80px 0 0 0;
  }

  /* Red page title */
  .hfps-red-title {
    font-family: 'Poppins', sans-serif;
    font-size: 32px;
    font-weight: 600;
    color: #EB2019;
    margin-bottom: 60px;
    line-height: 120%;
  }

  /* Two-col grid: left slider + right paragraphs */
  .hfps-top-grid {
    display: flex;
    gap: 89px;
    align-items: center;
    margin-bottom: 30px;
  }

  /* ── SLIDER ── */
  .hfps-slider {
    flex: 0 0 713px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .hfps-slider-img-wrap {
    width: 100%;
    height: 524px;
    border-radius: 10px;
    overflow: hidden;
    cursor: grab;
    user-select: none;
  }
  .hfps-slider-img-wrap:active { cursor: grabbing; }
  .hfps-slider-track {
    display: flex;
    height: 100%;
    transition: transform 0.6s ease;
  }
  .hfps-slider-track .hfps-slider-img { min-width: 100%; flex-shrink: 0; }
  .hfps-slider-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .hfps-slider-dots {
    display: flex;
    justify-content: center;
    gap: 8px;
  }
  .hfps-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #B8B7B7;
    border: none;
    cursor: pointer;
    padding: 0;
    transition: background 0.2s;
  }
  .hfps-dot.hfps-dot-active { background: #EB2019; }

  /* ── RIGHT PARAGRAPHS ── */
  .hfps-right-text {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  .hfps-para {
    font-family: 'Poppins', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #202020;
    line-height: 160%;
  }

  /* Full-width paragraphs below the grid */
  .hfps-full-paras {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 60px;
  }

  /* ══════════════════════════════════════════
     RED DIVIDER
  ══════════════════════════════════════════ */
  .hfps-divider {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 102px;
  }
  .hfps-divider hr {
    border: none;
    border-top: 1px solid #EB2019;
    opacity: 0.3;
  }

  /* ══════════════════════════════════════════
     4. ADVANTAGES SECTION
  ══════════════════════════════════════════ */
  .hfps-advantages {
    padding: 80px 0 120px 0;
  }
  .hfps-adv-heading {
    font-family: 'Poppins', sans-serif;
    font-size: 32px;
    font-weight: 600;
    color: #202020;
    text-align: center;
    margin-bottom: 60px;
  }
  .hfps-adv-pills {
    display: flex;
    gap: 2px;
    justify-content: center;
    flex-wrap: wrap;
  }
  .hfps-adv-pill {
    flex: 1;
    max-width: 279px;
    background: #FDEAEA;
    border-radius: 0px;
    padding: 26px 48px;
    text-align: center;
    font-family: 'Poppins', sans-serif;
    font-size: 20px;
    font-weight: 600;
    color: #EB2019;
    line-height: 140%;
  }
     .hfps-adv-pill:first-child {
     border-radius: 10px 0px 0px 10px;
}
      .hfps-adv-pill:last-child {
     border-radius: 0px 10px 10px 0px;
}

  /* ══════════════════════════════════════════
     RESPONSIVE — TABLET 768px
  ══════════════════════════════════════════ */
  @media (max-width: 768px) {
    .hfps-hero              { height: 300px; }
    .hfps-hero-bg           { display: block; }
    .hfps-hero-bg-mobile    { display: none; }
    .hfps-hero-content      { padding: 0 32px; }
    .hfps-hero-title        { font-size: 28px; max-width: 320px; }
    .hfps-hero-divider      { width: 260px; }
    .hfps-hero-desc         { font-size: 14px; }

    .hfps-breadcrumb        { padding: 28px 0 0 0; }
    .hfps-breadcrumb-inner  { padding: 0 32px; }
    .hfps-inner             { padding: 0 32px; }
    .hfps-divider           { padding: 0 32px; }

    .hfps-section-content   { padding: 40px 0 0 0; }
    .hfps-red-title         { font-size: 24px; margin-bottom: 30px; }

    .hfps-top-grid          { flex-direction: column; gap: 30px; }
    .hfps-slider            { flex: none; width: 100%; }
    .hfps-slider-img-wrap   { height: 260px; }

    .hfps-para              { font-size: 14px; }
    .hfps-full-paras        { margin-bottom: 40px; }

    .hfps-advantages        { padding: 40px 0 48px 0; }
    .hfps-adv-heading       { font-size: 22px; margin-bottom: 24px; }
    .hfps-adv-pill          { font-size: 12px; padding: 20px 14px; min-width: 120px; }
  }

  /* ══════════════════════════════════════════
     RESPONSIVE — MOBILE 480px
  ══════════════════════════════════════════ */
  @media (max-width: 480px) {
    .hfps-hero              { height: 400px; }
    .hfps-hero-bg           { display: none; }
    .hfps-hero-bg-mobile    { display: block; }
    .hfps-hero-content      { padding: 0 16px; }
    .hfps-hero-title        { font-size: 24px; max-width: 280px; }
    .hfps-hero-divider      { width: 100%; margin-bottom: 15px; }
    .hfps-hero-desc         { font-size: 14px; }

    .hfps-breadcrumb        { padding: 22px 0 0 0; }
    .hfps-breadcrumb-inner  { padding: 0 16px; gap: 16px; }
    .hfps-bc-item           { font-size: 14px; }
    .hfps-bc-sep            { width: 18px; height: 18px; margin: 0 4px; }
    .hfps-inner             { padding: 0 16px; }
    .hfps-divider           { padding: 0 16px; }

    .hfps-section-content   { padding: 30px 0 0 0; }
    .hfps-red-title         { font-size: 20px; margin-bottom: 20px; }
    .hfps-slider-img-wrap   { height: 260px; }
    .hfps-dot               { width: 6px; height: 6px; }

    .hfps-para              { font-size: 14px; }
    .hfps-full-paras        { margin-bottom: 60px; gap: 16px; }

    .hfps-advantages        { padding: 60px 0 60px 0; }
    .hfps-adv-heading       { font-size: 20px; margin-bottom: 30px; }
    .hfps-adv-pills         { gap: 1px; flex-direction:column; }
    .hfps-adv-pill:first-child { border-radius:0px;}
    .hfps-adv-pill:last-child { border-radius:0px;}
    .hfps-adv-pill          { font-size: 13px; padding: 16px 12px; min-width: 100%; }
  }
`;

export default function HFPS() {
  const [activeSlide, setActiveSlide] = useState(0);
  const intervalRef = useRef(null);
  const dragStartX  = useRef(null);

  /* ── Autoplay ── */
  const stopAutoplay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };
  const startAutoplay = () => {
    stopAutoplay();
    intervalRef.current = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % SLIDES.length);
    }, AUTOPLAY_SPEED);
  };
  useEffect(() => {
    startAutoplay();
    return () => stopAutoplay();
  }, []);

  /* ── Dot click ── */
  const goToSlide = (i) => {
    stopAutoplay();
    setActiveSlide(i);
    startAutoplay();
  };

  /* ── Drag / swipe ── */
  const onDragStart = (e) => {
    dragStartX.current = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
    stopAutoplay();
  };
  const onDragEnd = (e) => {
    if (dragStartX.current === null) return;
    const endX = e.type === "touchend" ? e.changedTouches[0].clientX : e.clientX;
    const diff  = dragStartX.current - endX;
    if (Math.abs(diff) > 80) {
      setActiveSlide(prev =>
        diff > 0
          ? (prev + 1) % SLIDES.length
          : (prev - 1 + SLIDES.length) % SLIDES.length
      );
    }
    dragStartX.current = null;
    startAutoplay();
  };

  return (
    <div className="hfps-wrap">
      <style>{CSS}</style>

      {/* ══ 1. HERO ══ */}
      <section className="hfps-hero">
        <img className="hfps-hero-bg"        src={HeroBgImg}       alt="" />
        <img className="hfps-hero-bg-mobile" src={HeroBgImgMobile} alt="" />
        <div className="hfps-hero-overlay" />
        <div className="hfps-hero-content">
          <h1 className="hfps-hero-title">High Frequency Power Supply For ESP</h1>
          <hr className="hfps-hero-divider" />
          <p className="hfps-hero-desc">
            he High Frequency system can be retrofitted to a precipitator electrical field through the use of existing control cabinets and existing TR's.
          </p>
        </div>
      </section>

      {/* ══ 2. BREADCRUMB ══ */}
      <nav className="hfps-breadcrumb" aria-label="Breadcrumb">
        <div className="hfps-breadcrumb-inner">
          <a href="/" className="hfps-bc-item">Home</a>
          <span className="hfps-bc-sep"><img src={BcSepIcon} alt="" /></span>
          <a href="/products" className="hfps-bc-item">Products</a>
          <span className="hfps-bc-sep"><img src={BcSepIcon} alt="" /></span>
          <span className="hfps-bc-item hfps-active">HFPS</span>
        </div>
      </nav>

      {/* ══ 3. CONTENT ══ */}
      <section className="hfps-section-content">
        <div className="hfps-inner">

          {/* Red page title */}
          <h2 className="hfps-red-title">High Frequency Power Supply For ESP</h2>

          {/* Two-col: slider left + paragraphs right */}
          <div className="hfps-top-grid">

            {/* Left — slider */}
            <div className="hfps-slider">
              <div
                className="hfps-slider-img-wrap"
                onMouseDown={onDragStart}
                onMouseUp={onDragEnd}
                onMouseLeave={() => { dragStartX.current = null; startAutoplay(); }}
                onTouchStart={onDragStart}
                onTouchEnd={onDragEnd}
              >
                <div
                  className="hfps-slider-track"
                  style={{ transform: `translateX(-${activeSlide * 100}%)` }}
                >
                  {SLIDES.map((src, i) => (
                    <img
                      key={i}
                      className="hfps-slider-img"
                      src={src}
                      alt={`Slide ${i + 1}`}
                      draggable={false}
                    />
                  ))}
                </div>
              </div>
              <div className="hfps-slider-dots">
                {SLIDES.map((_, i) => (
                  <button
                    key={i}
                    className={`hfps-dot${activeSlide === i ? " hfps-dot-active" : ""}`}
                    onClick={() => goToSlide(i)}
                    aria-label={`Slide ${i + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Right — two paragraphs */}
            <div className="hfps-right-text">
              <p className="hfps-para">
                The Electrostatic Precipitator (ESP) power supplies which are in use today operate at a fixed frequency of 50 Hz. In recent years various power supply designs using frequencies in the &gt;10,000 Hz range have been used for ESP's with the goal of improving ESP field efficiencies. The difference in performance is almost completely due to the low ripple voltage delivered by such power supplies. Ador's high frequency power supply can be operated in a range of 200 Hz to 1000 Hz. At the preferred frequency of 400 Hz the Ador system can achieve ripple reduction to negligible value while avoiding the complexity and reliability problems of higher frequency power supplies.
              </p>
              <p className="hfps-para">
                This control system provides the significant advantages of higher frequencies while permitting the use of existing transformer-rectifiers and existing interconnecting wiring. The control system can be housed in existing TR controls cabinets. If desired, this new control system may be fed from existing single phase supply or from 3 phase supply if available. The use of frequencies between 200 and 1000 Hz permits the reliability that is associated with conventional TR's.
              </p>
            </div>

          </div>

          {/* Full-width paragraphs below */}
          <div className="hfps-full-paras">
            <p className="hfps-para">
              A single phase or three phase AC main power source is fed into a rectifier and filter combination to create a relatively smooth DC power source. The use of an alternate single-phase supply requires additional components for filtering, yet is still viable within existing cabinets. This DC source is then fed into an IGBT full wave bridge circuit where it is converted into a high frequency AC waveform. The use of lower frequencies permits the use of the existing TR with the existing internal rectifiers and feedback signals. The value of the existing CLR for the conversion needs to be checked for possible modification. Typically lower CLR values are needed.
            </p>
            <p className="hfps-para">
              The High Frequency system can be retrofitted to a precipitator electrical field through the use of existing control cabinets and existing TR's.
            </p>
            <p className="hfps-para">
              The High frequency AC source created by the control circuit can feed into an existing electrostatic precipitator Transformer Rectifier or into a new, smaller and lighter specially designed, high frequency transformer rectifier.
            </p>
          </div>

        </div>
      </section>

      {/* ══ DIVIDER ══ */}
      <div className="hfps-divider"><hr /></div>

      {/* ══ 4. ADVANTAGES ══ */}
      <section className="hfps-advantages">
        <div className="hfps-inner">
          <h2 className="hfps-adv-heading">Advantages Of HFPS</h2>
          <div className="hfps-adv-pills">
            {ADVANTAGES.map((label, i) => (
              <div key={i} className="hfps-adv-pill">{label}</div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}